import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Modal, Button } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { showSuccess } from '@/shared/lib/toast-helpers';
import {
  useValidateProviderFeesCsv,
  useImportProviderFeesCsv,
  useDownloadProviderFeesCsvTemplate,
} from '@/billing/services/use-provider-fees-csv';
import type { ValidateCsvResponse } from '@/shared/types/billing';

/**
 * Provider Fees CSV import dialog. PM parity:
 * `import-provider-fees-csv-dialog.tsx`. Recomposed on Signal DS: shadcn
 * `Dialog`/`DialogContent` → DS `Modal`; shadcn `Button` → DS `Button`;
 * lucide file glyphs → plain text/emoji (DS registry has no file-upload
 * glyph — see report DS-GAP); `InvalidRowsCard` inlined (PA has no
 * `import-csv-dialog`). No hardcoded hex.
 *
 * 2-stage import flow:
 *   1. Drop / browse CSV →  POST /ProviderServiceFees/ValidateCsv/{providerId}
 *   2. If invalid rows >0 → show the invalid-rows card; user fixes + retries.
 *   3. If the provider already has fees → Override / Replace choice.
 *   4. Commit via POST /ProviderServiceFees/ImportCsv/{providerId}.
 */

interface ImportProviderFeesCsvDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string;
  /** Existing fee count for the provider — drives Override / Replace gate. */
  existingFeesCount?: number;
  onImported?: () => void;
}

type Step = 'pick' | 'choice';

export function ImportProviderFeesCsvDialog({
  open,
  onOpenChange,
  providerId,
  existingFeesCount = 0,
  onImported,
}: ImportProviderFeesCsvDialogProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [step, setStep] = useState<Step>('pick');
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<ValidateCsvResponse | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const validate = useValidateProviderFeesCsv();
  const importCsv = useImportProviderFeesCsv();
  const downloadTemplate = useDownloadProviderFeesCsvTemplate();
  // LOOP-SAFETY: stable mutate fns.
  const { mutate: validateCsv, reset: resetValidate } = validate;
  const { mutateAsync: importCsvAsync, reset: resetImport } = importCsv;
  const { mutateAsync: downloadTemplateAsync } = downloadTemplate;

  const reset = () => {
    setStep('pick');
    setFile(null);
    setValidation(null);
    setIsDragging(false);
    resetValidate();
    resetImport();
  };

  useEffect(() => {
    if (!open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => onOpenChange(false);

  const commit = async (
    removeExisting: boolean,
    v: ValidateCsvResponse | null = validation,
    f: File | null = file,
  ) => {
    if (!v?.requestId || !f) return;
    try {
      await importCsvAsync({
        providerId,
        requestId: v.requestId,
        fileName: v.originalFileName ?? f.name,
        removeExisting,
      });
      showSuccess('Provider fees successfully imported');
      onImported?.();
      handleClose();
    } catch {
      // Global toast surfaces the failure.
    }
  };

  const acceptFile = (next: File) => {
    if (!isCsvFile(next)) {
      toast.error('Only .csv files can be imported.');
      return;
    }
    setFile(next);
    setValidation(null);
    validateCsv(
      { providerId, csvFile: next },
      {
        onSuccess: (data) => {
          setValidation(data);
          if ((data.invalidRecords ?? 0) > 0) return;
          if (existingFeesCount > 0) setStep('choice');
          else void commit(true, data, next);
        },
      },
    );
  };

  const handleDownloadTemplate = async () => {
    try {
      const url = await downloadTemplateAsync();
      if (typeof url === 'string' && url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // Global toast surfaces the failure.
    }
  };

  // ── Drag-and-drop handlers ────────────────────────────────────────────
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    if (!isDragging) setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) acceptFile(dropped);
  };

  return (
    <Modal open={open} onClose={handleClose} title="Import Provider Fees" size="md">
      {step === 'pick' && (
        <div
          onDragEnter={handleDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'flex flex-col items-center text-center px-6 py-8 gap-6 rounded-md transition-colors',
            isDragging && 'bg-blue-100/40',
          )}
        >
          <span className="text-4xl" aria-hidden>
            🗂️
          </span>

          <p className="text-sm font-medium text-text-primary">
            {isDragging
              ? 'Drop the file to upload'
              : 'Drop Sheets Here or Use Browse Button'}
          </p>

          <p className="text-sm text-text-muted leading-relaxed max-w-sm">
            To import provider fees, please download{' '}
            <button
              type="button"
              onClick={handleDownloadTemplate}
              disabled={downloadTemplate.isPending}
              className="text-brand-primary underline hover:text-interactive-hover disabled:opacity-60"
            >
              this CSV
            </button>
            , paste your fees in the respective columns and upload to the system.
          </p>

          {file && (
            <div className="flex items-center gap-2 px-3 py-2 bg-grey-100 rounded-md text-sm text-text-secondary max-w-full">
              <span className="truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  setValidation(null);
                }}
                aria-label="Remove file"
                className="text-text-muted hover:text-text-secondary font-bold"
              >
                ✕
              </button>
            </div>
          )}

          {validation && (validation.invalidRecords ?? 0) > 0 && (
            <InvalidRowsCard validation={validation} />
          )}

          {validate.isPending || importCsv.isPending ? (
            <p className="text-sm text-text-muted">Processing…</p>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => {
                  const next = e.target.files?.[0];
                  if (next) acceptFile(next);
                  e.target.value = '';
                }}
              />
              <Button variant="primary" onClick={() => fileInputRef.current?.click()}>
                Browse
              </Button>
            </>
          )}
        </div>
      )}

      {step === 'choice' && validation && (
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <span className="flex items-center justify-center w-5 h-5 rounded bg-status-warning text-white text-sm font-bold flex-shrink-0">
              !
            </span>
            <p className="text-sm text-text-primary leading-snug">
              This provider already has fee overrides. How would you like to
              proceed with the new fees?
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <ChoiceCard
              title="Override"
              description="Match services by code and override existing prices. New services in the CSV are added."
              onClick={() => commit(false)}
              disabled={importCsv.isPending}
            />
            <ChoiceCard
              title="Replace"
              description="Remove all existing fee overrides for this provider, then import from CSV."
              onClick={() => commit(true)}
              disabled={importCsv.isPending}
            />
          </div>

          {importCsv.isPending && (
            <p className="text-sm text-text-muted text-center">Processing…</p>
          )}
        </div>
      )}
    </Modal>
  );
}

interface ChoiceCardProps {
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
}

function ChoiceCard({ title, description, onClick, disabled }: ChoiceCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-start gap-2 p-4 text-left bg-surface-card border border-border-default rounded-lg transition-all hover:border-brand-primary hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
    >
      <span className="text-sm font-semibold text-brand-depth">{title}</span>
      <span className="text-xs text-text-muted leading-snug">{description}</span>
    </button>
  );
}

// ─── Invalid-rows card (inlined; PA has no import-csv-dialog) ─────────────

function InvalidRowsCard({ validation }: { validation: ValidateCsvResponse }) {
  const [expanded, setExpanded] = useState(false);
  const invalidCount = validation.invalidRecords ?? 0;
  const errorEntries = Object.entries(validation.invalidRecordsErrors ?? {})
    .map(([row, errs]) => ({
      row,
      messages: (Array.isArray(errs) ? errs : [errs])
        .map((m) => (typeof m === 'string' ? m : JSON.stringify(m)))
        .filter(Boolean),
    }))
    .filter((e) => e.messages.length > 0);

  return (
    <div
      role="alert"
      className="w-full rounded-lg border border-status-error/40 bg-status-error-bg/50 p-3 text-left flex flex-col gap-2"
    >
      <p className="text-sm text-text-primary leading-relaxed">
        {invalidCount} item{invalidCount === 1 ? '' : 's'} haven&rsquo;t been
        uploaded. Download the CSV to review the missing fields. Once you fix
        those, reimport the file and we&rsquo;ll update the list. No duplicates
        will be created.
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        {validation.invalidCsv && (
          <a
            href={validation.invalidCsv}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-brand-primary hover:underline"
          >
            Download CSV
          </a>
        )}
        {errorEntries.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-sm font-medium text-text-secondary hover:underline"
            aria-expanded={expanded}
          >
            {expanded ? 'Hide details' : 'View details'}
          </button>
        )}
      </div>
      {expanded && errorEntries.length > 0 && (
        <ul className="max-h-40 overflow-y-auto text-xs text-text-secondary border-t border-status-error/20 pt-2 mt-1 flex flex-col gap-2">
          {errorEntries.map(({ row, messages }) => (
            <li key={row} className="flex flex-col gap-1">
              <span className="font-medium text-text-primary">Row {row}</span>
              <ul className="pl-3 list-disc list-outside marker:text-status-error">
                {messages.map((msg, i) => (
                  <li key={i}>{msg}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function isCsvFile(f: File): boolean {
  const lowerName = f.name.toLowerCase();
  return (
    lowerName.endsWith('.csv') ||
    f.type === 'text/csv' ||
    f.type === 'application/vnd.ms-excel'
  );
}
